import React, { useState, useEffect } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  Avatar,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SHIFT_DEFINITIONS = [
  { time: "7:30 AM", name: "BKF" },
  { time: "8:30 AM", name: "CHECK-IN" },
  { time: "9:00 AM", name: "P. GROUP" },
  { time: "10:30 AM", name: "CBT/ 8 Step" },
  { time: "11:40 AM", name: "CBT / Holistic" },
  { time: "12:30 PM", name: "Lunch" },
  { time: "1:15 PM", name: "Group" },
  { time: "3:00 PM", name: "Community" },
  { time: "4:00 PM", name: "Afternoon Sessions" },
  { time: "5:00 PM", name: "SWOT/ 12Step" },
  { time: "6:00 PM", name: "Dinner" },
  { time: "7:00 PM", name: "Meeting" },
];

function GroupAttendanceModalMobile({ open, onClose, formData }) {
  const [selectedShift, setSelectedShift] = useState(0);
  const [localAttendances, setLocalAttendances] = useState(
    formData.attendances || Array.from({ length: 11 }, () => ({}))
  );
  const [inhouseClients, setInhouseClients] = useState([]);

  // Pour gérer les modes d'édition et les notes locales, avec des clés du type "clientId_depart" et "clientId_arrived"
  const [editModes, setEditModes] = useState({});
  const [localNotes, setLocalNotes] = useState({});

  const [enlargedImage, setEnlargedImage] = useState(null);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Récupération de la note de départ pour un client
  const getClientNote = (clientId) => {
    return (
      (localAttendances &&
        localAttendances[selectedShift] &&
        localAttendances[selectedShift][clientId] &&
        localAttendances[selectedShift][clientId].note) ||
      ""
    );
  };

  // Récupération de la note d'arrivée pour un client
  const getClientNoteArrived = (clientId) => {
    return (
      (localAttendances &&
        localAttendances[selectedShift] &&
        localAttendances[selectedShift][clientId] &&
        localAttendances[selectedShift][clientId].noteArrived) ||
      ""
    );
  };

  // Mise à jour du state localNotes lors de la modification d'un TextField
  const handleNoteChange = (key, event) => {
    const newText = event.target.value;
    setLocalNotes((prev) => ({
      ...prev,
      [key]: newText,
    }));
  };

  // Bascule du mode édition et sauvegarde la note dans Firestore,
  // en ajoutant les infos client (nom, prénom, id, photo) si besoin.
  const handleToggleEdit = async (clientId, isArrived = false) => {
    const key = clientId + (isArrived ? "_arrived" : "_depart");
    const isEditing = editModes[key] || false;
    if (isEditing) {
      const updatedAttendances = localAttendances
        ? [...localAttendances]
        : Array.from({ length: 11 }, () => ({}));
      const currentShiftData = updatedAttendances[selectedShift] || {};

      // Récupérer les infos du client à partir de la liste inhouse
      const clientInfo = inhouseClients.find((c) => c.id === clientId) || {};
      const existingAttendance = currentShiftData[clientId] || {
        id: clientId,
        firstName: clientInfo.firstName || "",
        lastName: clientInfo.lastName || "",
        profilePicture: clientInfo.profilePicture || "",
      };

      const newNote =
        localNotes[key] !== undefined
          ? localNotes[key]
          : isArrived
          ? getClientNoteArrived(clientId)
          : getClientNote(clientId);

      currentShiftData[clientId] = {
        ...existingAttendance,
        ...(isArrived ? { noteArrived: newNote } : { note: newNote }),
      };
      updatedAttendances[selectedShift] = currentShiftData;
      setLocalAttendances(updatedAttendances);
      const docRef = doc(db, "groupAttendanceForms", formData.id);
      try {
        await updateDoc(docRef, { attendances: updatedAttendances });
      } catch (error) {
        console.error("Error saving note:", error);
      }
    }
    setEditModes((prev) => ({
      ...prev,
      [key]: !isEditing,
    }));
  };

  useEffect(() => {
    const docRef = doc(db, "groupAttendanceForms", formData.id);
    const unsubscribe = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        const attendancesFromFirestore = docSnap.data().attendances;
        // Comparer pour n'update que si les données sont différentes
        if (
          JSON.stringify(attendancesFromFirestore) !==
          JSON.stringify(localAttendances)
        ) {
          setLocalAttendances(attendancesFromFirestore);
        }
      }
    });
    return () => unsubscribe();
  }, [formData.id, localAttendances]);

  const handleAvatarClick = (client) => {
    const url =
      client.profilePicture ||
      "https://via.placeholder.com/150/CCCCCC/FFFFFF/?text=Avatar";
    setEnlargedImage(url);
    setSelectedClient(client);
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setEnlargedImage(null);
    setSelectedClient(null);
  };

  useEffect(() => {
    setLocalAttendances(
      formData.attendances || Array.from({ length: 11 }, () => ({}))
    );
  }, [formData]);

  // Récupération des clients inhouse depuis Firestore
  useEffect(() => {
    async function fetchInhouseClients() {
      const clientsRef = collection(db, "clients");
      const clientQuery = query(
        clientsRef,
        where("clientStatus", "==", "In House")
      );
      const snapshot = await getDocs(clientQuery);
      const clientsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInhouseClients(clientsData);
    }
    fetchInhouseClients();
  }, []);

  const handleShiftChange = (event) => {
    setSelectedShift(parseInt(event.target.value, 10));
  };

  // Mise à jour de l'attendance (checkbox) en ajoutant les infos client si non présentes
  const handleCheckboxChange = async (clientId, type, event) => {
    const newValue = event.target.checked;
    const updatedAttendances = localAttendances
      ? [...localAttendances]
      : Array.from({ length: 11 }, () => ({}));
    const currentShiftData = updatedAttendances[selectedShift] || {};
    const clientInfo = inhouseClients.find((c) => c.id === clientId) || {};
    const existingAttendance = currentShiftData[clientId] || {
      id: clientId,
      firstName: clientInfo.firstName || "",
      lastName: clientInfo.lastName || "",
      profilePicture: clientInfo.profilePicture || "",
    };
    currentShiftData[clientId] = {
      ...existingAttendance,
      [type]: newValue,
    };
    updatedAttendances[selectedShift] = currentShiftData;
    setLocalAttendances(updatedAttendances);
    try {
      const docRef = doc(db, "groupAttendanceForms", formData.id);
      await updateDoc(docRef, { attendances: updatedAttendances });
    } catch (error) {
      console.error("Error updating attendance:", error);
    }
  };

  const getCheckboxValue = (clientId, type) => {
    if (!localAttendances || !localAttendances[selectedShift]) return false;
    const clientAttendance = localAttendances[selectedShift][clientId];
    return clientAttendance ? clientAttendance[type] || false : false;
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            {`GAR - ${formData.date}`}
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <FormControl
          sx={{
            minWidth: "50%",
            backgroundColor: "white",
            borderRadius: 1,
            marginBottom: "5px",
          }}
        >
          <InputLabel id="shift-select-mobile-label">Shift</InputLabel>
          <Select
            labelId="shift-select-mobile-label"
            value={selectedShift}
            label="Shift"
            onChange={handleShiftChange}
          >
            {SHIFT_DEFINITIONS.map((shift, index) => (
              <MenuItem key={index} value={index}>
                {`Shift ${index + 1} - ${shift.time} - ${shift.name}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <List>
          {inhouseClients.map((client) => (
            <ListItem
              key={client.id}
              button
              style={{
                border: "1px solid lightgrey",
                borderRadius: "5px",
                marginBottom: "5px",
                backgroundColor:
                  getCheckboxValue(client.id, "incoming") &&
                  getCheckboxValue(client.id, "arrived")
                    ? "lightgreen"
                    : "inherit",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginRight: "8px",
                }}
                onClick={() => handleAvatarClick(client)}
              >
                <Avatar
                  src={
                    client.profilePicture ||
                    "https://via.placeholder.com/40/CCCCCC/FFFFFF/?text=Avatar"
                  }
                  alt={`${client.firstName} ${client.lastName}`}
                  sx={{ mb: 1 }}
                />
                <Typography
                  variant="body2"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "60px",
                  }}
                >
                  {client.firstName} {client.lastName}
                </Typography>
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={getCheckboxValue(client.id, "incoming")}
                      onChange={(e) =>
                        handleCheckboxChange(client.id, "incoming", e)
                      }
                    />
                  }
                  label="Incoming"
                />
                {/* Section Note de Départ */}
                {editModes[client.id + "_depart"] ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <TextField
                      multiline
                      rows={2}
                      value={
                        localNotes[client.id + "_depart"] !== undefined
                          ? localNotes[client.id + "_depart"]
                          : getClientNote(client.id)
                      }
                      onChange={(e) =>
                        handleNoteChange(client.id + "_depart", e)
                      }
                      variant="outlined"
                      size="small"
                      style={{
                        flex: 1,
                        maxWidth: "200px",
                        height: "48px",
                        overflow: "auto",
                      }}
                      InputProps={{
                        style: {
                          padding: "4px 8px",
                          fontSize: "0.875rem",
                          lineHeight: 1.5,
                        },
                      }}
                    />
                    <IconButton
                      onClick={() => handleToggleEdit(client.id, false)}
                      size="small"
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <TextField
                      value={getClientNote(client.id) || "No note"}
                      variant="outlined"
                      size="small"
                      disabled
                      multiline
                      InputProps={{
                        style: {
                          padding: "4px 8px",
                          fontSize: "0.875rem",
                          lineHeight: 1.5,
                          overflow: "auto",
                        },
                      }}
                      style={{ flex: 1, maxWidth: "200px", height: "48px" }}
                    />
                    <IconButton
                      onClick={() => handleToggleEdit(client.id, false)}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </div>
                )}
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={getCheckboxValue(client.id, "arrived")}
                      onChange={(e) =>
                        handleCheckboxChange(client.id, "arrived", e)
                      }
                    />
                  }
                  label="Arrived"
                />
                {/* Section Note d'Arrivée */}
                {editModes[client.id + "_arrived"] ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <TextField
                      multiline
                      rows={2}
                      value={
                        localNotes[client.id + "_arrived"] !== undefined
                          ? localNotes[client.id + "_arrived"]
                          : getClientNoteArrived(client.id)
                      }
                      onChange={(e) =>
                        handleNoteChange(client.id + "_arrived", e)
                      }
                      variant="outlined"
                      size="small"
                      style={{
                        flex: 1,
                        maxWidth: "200px",
                        height: "48px",
                        overflow: "auto",
                      }}
                      InputProps={{
                        style: {
                          padding: "4px 8px",
                          fontSize: "0.875rem",
                          lineHeight: 1.5,
                        },
                      }}
                    />
                    <IconButton
                      onClick={() => handleToggleEdit(client.id, true)}
                      size="small"
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <TextField
                      value={getClientNoteArrived(client.id) || "No note"}
                      variant="outlined"
                      size="small"
                      disabled
                      multiline
                      InputProps={{
                        style: {
                          padding: "4px 8px",
                          fontSize: "0.875rem",
                          lineHeight: 1.5,
                          overflow: "auto",
                        },
                      }}
                      style={{ flex: 1, maxWidth: "200px", height: "48px" }}
                    />
                    <IconButton
                      onClick={() => handleToggleEdit(client.id, true)}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </div>
                )}
              </div>
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      <Dialog
        open={openImageDialog}
        onClose={handleCloseImageDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent style={{ textAlign: "center" }}>
          <img
            src={enlargedImage}
            alt="Enlarged Avatar"
            style={{ width: "100%", maxWidth: "300px" }}
          />
          {selectedClient && (
            <Typography variant="h6" style={{ marginTop: "8px" }}>
              {selectedClient.firstName} {selectedClient.lastName}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <IconButton onClick={handleCloseImageDialog}>
            <CloseIcon />
          </IconButton>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default GroupAttendanceModalMobile;
